import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { TournamentSeason } from '../models/tournament-season';
import { ConfigService } from '../services/config.service';
import { map } from 'rxjs/operators';
import { Game, GameStatuses } from '../models/game';
import { Tournament } from '../models/tournament';
import { applyGamesFilters, TournamentEventsListFilter, TournamentGamesFilters } from './tournament-api';
import { PaginatedResponse } from './paginated-response.interface';
import { TournamentEvent, TournamentEventTypes } from '../models/tournament-event';
import { applyGameMediaFilters, GameMediaFilters } from './media-api';
import { TournamentTeam } from '../models/tournament-team';
import { GameTimelineStages } from '../models/game-timeline-stages';
import { Playoff } from '../models/playoff';

@Injectable({providedIn: 'root'})
export class TournamentSeasonApi {
  constructor(
    private httpClient: HttpClient,
    private configService: ConfigService,
  ) {
  }

  async getByAlias(leagueId: number, alias: string): Promise<TournamentSeason> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/league/${leagueId}/tournament_seasons/${alias}/`).pipe(
      map(result => TournamentSeason.toFront(result))
    ).toPromise();
  }

  async getTournaments(seasonId: number): Promise<Tournament[]> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament_season/${seasonId}/tournaments/`).pipe(
      map(result => Tournament.toFront(result))
    ).toPromise();
  }

  async getGames(seasonId: number, page: number, size: number, filters: TournamentGamesFilters): Promise<PaginatedResponse<Game[]>> {
    let params = new HttpParams().set('page', page.toString());
    if (size) {
      params = params.set('size', size.toString());
    }
    params = applyGamesFilters(filters, params);
    return this.httpClient.get(
      `${this.configService.get('apiUrl')}/api/v1/tournament_season/${seasonId}/games/`,
      {params, observe: 'response'}
    ).pipe(
      map(result => ({
        total: +result.headers.get('X-Page-Count'),
        data: Game.toFront(result.body)
      }))
    ).toPromise();
  }

  async getEvents(seasonId: number, page: number, size: number, filters?: TournamentEventsListFilter): Promise<PaginatedResponse<TournamentEvent[]>> {
    let params = new HttpParams().set('page', page.toString())
      .set('size', size.toString());
    if (filters) {
      if (filters.event) {
        params = params.set('event', TournamentEventTypes[filters.event]);
      }
      if (filters.search) {
        params = params.set('search', filters.search);
      }
      if (filters.tournamentTour) {
        params = params.set('tournament_tour', filters.tournamentTour.toString());
      }
    }
    return this.httpClient.get(
      `${this.configService.get('apiUrl')}/api/v1/tournament_season/${seasonId}/events/`,
      {params, observe: 'response'}
    )
      .pipe(map(result => ({
        total: +result.headers.get('X-Page-Count'),
        data: TournamentEvent.toFront(result.body)
      })))
      .toPromise();
  }

  async getGameMedia(seasonId: number, page: number, size: number, filters: GameMediaFilters): Promise<PaginatedResponse<Game[]>> {
    let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
    params = applyGameMediaFilters(filters, params);

    return this.httpClient.get(
      `${this.configService.get('apiUrl')}/api/v1/tournament_season/${seasonId}/game_media/`,
      {observe: 'response', params})
      .pipe(
        map(response => {
          return {
            total: +response.headers.get('X-Page-Count'),
            data: Game.toFront(response.body)
          };
        })
      ).toPromise();
  }

  async getTeams(seasonId: number, short?: boolean, micro?: boolean): Promise<TournamentTeam[]> {
    let params = new HttpParams();
    if (short) {
      params = params.set('short', '1');
    }
    if (micro) {
      params = params.set('micro', '1');
    }
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament_season/${seasonId}/teams/`, {params}).pipe(
      map(result => TournamentTeam.toFront(result))
    ).toPromise();
  }

  async getPlayoffs(seasonId: number): Promise<Playoff[]> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament_season/${seasonId}/playoffs/`).pipe(
      map(result => Playoff.toFront(result))
    ).toPromise();
  }

  async getGamesStages(seasonId: number): Promise<GameTimelineStages> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament_season/${seasonId}/games_stages/`)
      .pipe(map(data => GameTimelineStages.toFront(data)))
      .toPromise();
  }
}
